import { Routes } from "@angular/router";
import { AuthGuard } from "./guards/auth.guard";
import { CfaAuthorPublished } from "./guards/cfa-author-published.guard";
import { AnonGuard } from "./guards/anon.guard";
import { OnBoardingPending } from "./guards/onboarding-pending.guard";
import { OnboardingCompleted } from "./guards/onboarding-completed.guard";
import { DesktopGuard } from "./guards/desktop.guard";
import { CompositeGuard } from "./guards/composite.guard";

export const routes: Routes = [
    {
        path: "",
        canActivate: [CompositeGuard],
        data: {
            guards: [AuthGuard, CfaAuthorPublished],
            pageName: "Home"
        },
        loadComponent: () =>
            import("./components/home/home.component").then(
                (c) => c.HomeComponent
            ),
    },
    {
        path: "home",
        canActivate: [CompositeGuard],
        data: {
            guards: [AuthGuard, CfaAuthorPublished],
            pageName: "Home"
        },
        loadComponent: () =>
            import("./components/home/home.component").then(
                (c) => c.HomeComponent
            ),
    },
    {
        path: "login",
        canActivate: [AnonGuard],
        data: {
            pageName: "Login"
        },
        loadComponent: () =>
            import("./components/login/login.component").then(
                (c) => c.LoginComponent
            ),
    },
    {
        path: "onboarding",
        canActivate: [OnBoardingPending],
        data: {
            pageName: "Onboarding"
        },
        loadComponent: () =>
            import("./components/onboarding/onboarding.component").then(
                (c) => c.OnboardingComponent
            ),
    },
    {
        path: "email-onboarding",
        canActivate: [CompositeGuard],
        data: {
            guards: [AuthGuard, OnboardingCompleted, DesktopGuard],
            pageName: "Email Animation"
        },
        loadComponent: () =>
            import("./components/email-onboarding/email-onboarding.component").then(
                (c) => c.EmailOnboardingComponent
            ),
    },
    {
        path: "magic",
        loadComponent: () =>
            import("./components/magic-link/magic-link.component").then(
                (c) => c.MagicLinkComponent
            ),
        data: {
            pageName: "Magic Link"
        }
    },
    {
        path: "author-page/publishing",
        canActivate: [CompositeGuard],
        data: {
            guards: [AuthGuard, OnboardingCompleted],
            pageName: "Author Page Publishing"
        },
        loadComponent: () =>
            import(
                "./components/author-page-publishing/author-page-publishing.component"
            ).then((c) => c.AuthorPagePublishingComponent),
    },
    {
        path: "author-page",
        canActivate: [CompositeGuard],
        data: {
            guards: [AuthGuard, OnboardingCompleted, DesktopGuard],
            pageName: "Author Page"
        },
        loadComponent: () =>
            import(
                "./components/author-page-builder/author-page-builder.component"
            ).then((c) => c.AuthorPageBuilderComponent),
    },
    {
        path: "subscribers",
        canActivate: [CompositeGuard],
        data: {
            guards: [AuthGuard, CfaAuthorPublished, DesktopGuard],
            pageName: "Subscriber List"
        },
        loadComponent: () => import("./components/subscribers-page/subscribers-page.component").then((c) => c.SubscribersPageComponent),
    },
    {
        path: "templates",
        canActivate: [CompositeGuard],
        data: {
            guards: [AuthGuard, DesktopGuard],
            pageName: "Templates"
        },
        loadComponent: () => import("./components/templates-page/templates-page.component").then((c) => c.TemplatesPageComponent),
    },
    {
        path: "campaigns",
        canActivate: [CompositeGuard],
        data: {
            guards: [AuthGuard, CfaAuthorPublished, DesktopGuard],
            pageName: "Campaign List"
        },
        loadComponent: () => import("./components/campaigns-page/campaigns-page.component").then((c) => c.CampaignsPageComponent),
    },
    {
        path: "campaigns/resend/:taskId",
        canActivate: [CompositeGuard],
        data: {
            guards: [AuthGuard, CfaAuthorPublished, DesktopGuard],
            pageName: "Campaign Resend Animation"
        },
        loadComponent: () => import("./components/campaign-resend-animation/campaign-resend-animation.component").then((c) => c.CampaignResendAnimationComponent)
    },
    {
        path: "campaigns/:campaignId",
        canActivate: [CompositeGuard],
        data: {
            guards: [AuthGuard, CfaAuthorPublished, DesktopGuard],
            pageName: "Email Editor"
        },
        loadComponent: () => import("./components/new-email/new-email.component").then((c) => c.NewEmailComponent)
    },
    {
        path: "automations/:automationId",
        canActivate: [CompositeGuard],
        data: {
            guards: [AuthGuard, CfaAuthorPublished, DesktopGuard],
            pageName: "Automation Editor"
        },
        loadComponent: () => import("./components/automation-editor/automation-editor.component").then((c) => c.AutomationEditorComponent)
    },
    {
        path: "settings",
        canActivate: [CompositeGuard],
        data: {
            guards: [AuthGuard, OnboardingCompleted, DesktopGuard],
            pageName: "Settings"
        },
        loadComponent: () =>
            import(
                "./components/settings/settings.component"
            ).then((c) => c.SettingsComponent),
    },
    {
        path: "integrations",
        canActivate: [CompositeGuard],
        data: {
            guards: [AuthGuard, OnboardingCompleted, DesktopGuard],
            pageName: "Integrations"
        },
        loadComponent: () =>
            import(
                "./components/integrations/integrations.component"
            ).then((c) => c.IntegrationsComponent),
    },
    {
        path: "books",
        canActivate: [CompositeGuard],
        data: {
            guards: [AuthGuard, CfaAuthorPublished, DesktopGuard],
            pageName: "Book List"
        },
        loadComponent: () =>
            import(
                "./components/book-list/book-list.component"
            ).then((c) => c.BookListComponent),
    },
    {
        path: "new/email",
        canActivate: [CompositeGuard],
        data: {
            guards: [AuthGuard, CfaAuthorPublished, DesktopGuard],
            pageName: "Email Editor"
        },
        loadComponent: () =>
            import(
                "./components/new-email/new-email.component"
            ).then((c) => c.NewEmailComponent),
    },
    {
        path: "new/welcome-sequence",
        canActivate: [CompositeGuard],
        data: {
            guards: [AuthGuard, CfaAuthorPublished, DesktopGuard],
            pageName: "New Welcome Sequence"
        },
        loadComponent: () =>
            import(
                "./components/welcome-sequence/welcome-sequence.component"
            ).then((c) => c.NewWelcomeSequence),
    },
    {
        path: "new/:type",
        canActivate: [CompositeGuard],
        data: {
            guards: [AuthGuard, CfaAuthorPublished, DesktopGuard],
            pageName: "New Book"
        },
        loadComponent: () =>
            import(
                "./components/new-book/new-book.component"
            ).then((c) => c.NewBookComponent),
    },
    {
        path: "edit/:slug",
        canActivate: [CompositeGuard],
        data: {
            guards: [AuthGuard, CfaAuthorPublished, DesktopGuard],
            pageName: "Edit Book"
        },
        loadComponent: () =>
            import(
                "./components/edit-book/edit-book.component"
            ).then((c) => c.EditBookComponent),
    },
    {
        path: "giveaways/:giveawaySlug/invite/:token",
        data: {
            pageName: "Giveaway Invite"
        },
        loadComponent: () => import("./components/giveaways-invite-accept/giveaways-invite-accept.component").then((c) => c.GiveawaysInviteAcceptComponent)
    },
    {
        path: "giveaways/:giveawayId/promo-kit",
        canActivate: [CompositeGuard],
        data: {
            guards: [AuthGuard, CfaAuthorPublished, DesktopGuard],
            pageName: "Giveaway Promo Kit"
        },
        loadComponent: () => import("./components/giveaway-promo-kit/giveaway-promo-kit.component").then((c) => c.GiveawayPromoKitComponent)
    },
    {
        path: "giveaways/:giveawayId",
        canActivate: [CompositeGuard],
        data: {
            guards: [AuthGuard, CfaAuthorPublished, DesktopGuard],
            pageName: "Giveaways Dashboard"
        },
        loadComponent: () => import("./components/giveaways-dashboard/giveaways-dashboard.component").then((c) => c.GiveawaysDashboardComponent)
    },
    {
        path: "giveaways/:giveawayId/add-book",
        canActivate: [CompositeGuard],
        data: {
            guards: [AuthGuard, CfaAuthorPublished, DesktopGuard],
            pageName: "Giveaways Add Book"
        },
        loadComponent: () => import("./components/giveaways-add-book/giveaways-add-book.component").then((c) => c.GiveawaysAddBookComponent)
    },
    {
        path: "desktop",
        canActivate: [AuthGuard],
        data: {
            pageName: "Desktop Check"
        },
        loadComponent: () => import("./components/desktop-check/desktop-check.component").then((c) => c.DesktopCheckComponent),
    },
    {
        path: "**",
        redirectTo: "/author-page",
    }
];
